import { GRAPH_INTERVAL } from '@const/pageResponse.constants';
import { TIMESERIES_TOP_ORDER } from '@const/timeseriesRequest.contants';
import { IMetric } from '@dto/architecture.dto';
import { ExportTimeseriesRequestDTO } from '@dto/exporter.dto';
import { Metric } from '@dto/pageResponse.dto';
import { DeletedItems, TimeseriesRequestDTO, TimeseriesRequestParams, TimeseriesTopOrder } from '@dto/timeseriesRequest.dto';
import { TimeseriesResponseDTO } from '@dto/timeseriesResponse.dto';
import { DateUtil } from '@dto/util/DateUtil';
import { MenuProps, Modal, Spin } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import axios, { AxiosError, AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import type { Config, Icon, ModeBarButton, ModeBarButtonAny, PlotHoverEvent, PlotRelayoutEvent, ScatterData } from 'plotly.js';
import Plotly from 'plotly.js/lib/core';
import React, { ReactElement, useCallback, useContext, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconType } from 'react-icons';
import { FaAngleDoubleDown, FaExpand, FaRegTrashAlt, FaStar } from 'react-icons/fa';
import { FaChartLine } from 'react-icons/fa6';
import { MdLineAxis, MdOutlineFileDownload, MdOutlineVerticalAlignTop } from 'react-icons/md';
import { useResizeDetector } from 'react-resize-detector';
import { IsRegroupActive } from 'src/content/basePage/BasePage';
import { API_URL } from 'src/data/Api';
import { Log } from 'src/service/log.service';
import { postApi } from 'src/util/apiCalls';
import { GLOB } from 'src/util/Glob';
import { v4 } from 'uuid';
import { useAppGlobStore } from '../../appGlobalStore';
import Plot from '../../PlotlyFactory';
import { saveBlobToFile } from '../apiDownloader/XmApiDownloader';
import { XmSpinner } from '../spinner/XmSpinner';
import './graph.less';
import { GraphBtns, setToggled } from './GraphBtns';
import { GraphLegend } from './GraphLegend';
import { GraphPlotUtil } from './GraphPlotUtil';
import { GraphTable } from './GraphTable';
import { CollapsedGraphMenuContext, GraphHeightContext, GraphProps, GraphState, GraphType, KTM, PlotEl, RegroupContext, SharedYRangeContext, StartEnd, createPlotlyLayout, createYaxis, getMinYRange, hasFixedPercentRange, metricUnitLabel } from './GraphTypes';
import { DeletedItemsModal } from './modal/DeletedItemsModal';
import GraphDashboardSelect from './modal/GraphDashboardSelect';
import { TopItemsModal } from './modal/TopItemsModal';
import { YaxisModal } from './modal/YaxisModal';

interface IconProps {
  attr?: {
    viewBox?: string;
  };
  children?: {
    props?: {
      d?: string;
    };
  }[];
}

const X_FORMAT = `${DateUtil.DATETIME_FORMAT}:ss.SSS`;

/** min zoom intervals (ms) the user was already notified about, once per session */
const minZoomNotified = new Set<number>();

/** Default minimum zoom interval in ms is 1 hour */
const MIN_ZOOM_INTERVAL = 1000 * 60 * 60;
/** Minimum zoom interval for yearly in ms is 2 days */
const MIN_ZOOM_INTERVAL_YEAR = 1000 * 60 * 60 * 24 * 2;
/** relayout keys of a manual y range on any y axis, e.g. `yaxis.range[0]`, `yaxis2.range[1]` */
const Y_RANGE_KEY = /^yaxis\d*\.range\[/;

const BTN_NAME_LOAD_ALL = 'loadAll';

/** Headroom above/below data on a Y range shared between graphs, close to Plotly autorange padding */
const SHARED_Y_PAD = 0.05;

/** Plot elements with the wheel guard attached, so a re-attached ref does not add a second listener */
const wheelGuarded = new WeakSet<PlotEl>();

export function getTopItems(): number {
  return GLOB.userInfo.configuration.top ?? GLOB.graphTopItems;
}

function convertIconToPlotly(iconType: IconType): Icon {
  const iconProps = (iconType(null) as ReactElement<IconProps>).props;

  const iconViewBox = iconProps.attr.viewBox.split(' ');
  return {
    name: iconType.name,
    height: parseFloat(iconViewBox[3]),
    width: parseFloat(iconViewBox[2]),
    path: iconProps.children.at(-1).props.d
  };
}

export const XmGraph: React.FC<GraphProps> = (props) => {
  const { box, type, common } = props;
  const [modalFullscreenVisible, setModalFullscreenVisible] = useState(false);
  // the fullscreen copy keeps the full button row, only the page's own graphs fold it
  const collapsedMenu = useContext(CollapsedGraphMenuContext) && type === GraphType.regular;
  const [modalOtherOpen, setModalOtherOpen] = useState(false);
  const [modalAxisOpen, setModalAxisOpen] = useState(false);
  const [modalDeletedOpen, setModalDeletedOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Partial<ScatterData>[]>([]);
  const [tableData, setTableData] = useState<KTM[]>([]);
  const [zooming, setZooming] = useState(false);
  /** y-only zoom (axis drag or wheel) does not reload data, it only highlights the reset button */
  const [yZoomed, setYZoomed] = useState(false);
  const zoomed = zooming || yZoomed;
  const [plotVisible, setPlotVisible] = useState(true);
  // the latest responses, not a render's: a refresh queued just before a new response lands
  // would otherwise rebuild the plot from the old one
  const responsesRef = useRef<AxiosResponse<TimeseriesResponseDTO>[]>([]);
  const plotID = useMemo(() => v4(), []);
  const { hover, ...regroupedProps } = useContext(RegroupContext);
  // updateData runs from refreshPlots' timeout, in a closure that may predate the last hide
  const hiddenItemsRef = useRef(regroupedProps.hiddenItems);
  hiddenItemsRef.current = regroupedProps.hiddenItems;
  const aborter = useRef<AbortController>(undefined);
  const dataAborter = useRef<AbortController>(undefined);
  const initialized = useRef(false);
  const isRegroupActive = useContext(IsRegroupActive);
  const graphHeightOverride = useContext(GraphHeightContext);
  const isDragging = useRef(false);
  const isMobile = useAppGlobStore(s => s.isMobile);
  const sharedY = useContext(SharedYRangeContext);
  /** percent graphs already share the fixed 0-100 range */
  const sharesYRange = !!sharedY && type === GraphType.regular && !hasFixedPercentRange(box.metrics);
  /** shared range only in the initial view, zoomed graphs (either axis) keep their own range */
  const sharedActive = sharesYRange && !zoomed;
  const yUnitKey = metricUnitLabel(box.metrics);
  const sharedExtremes = sharesYRange ? sharedY.extremes[yUnitKey] : undefined;
  /** latest shared max for tick computation triggered from stale closures (legend, modals) */
  const sharedMaxRef = useRef<number>(undefined);
  sharedMaxRef.current = sharedExtremes?.max;
  /** shared range is pinned on the axis, a zoom has to hand it back to autorange */
  const sharedApplied = useRef(false);

  const [state] = useState<GraphState>(() => props.state ||
    ({
      additionalYaxis: null,
      deletedItems: undefined,
      endSec: props.end,
      hoverMode: 'closest',
      loadedItems: GraphPlotUtil.isOthersAllowed(box) ? getTopItems() : 0,
      orderBy: box.sort?.order || GLOB.userInfo?.configuration?.graphLegendOrderBy || TIMESERIES_TOP_ORDER.avg,
      othersCount: null,
      othersTooltip: null,
      plotlyRef: null,
      sortMetric: box.sort?.metric,
      stacked: true,
      startSec: props.start,
      tableRowKeys: [],
      unitSize: '',
      uuids: null,
    }));
  const [graphUtil] = useState(() => new GraphPlotUtil(box, common, props.data, state));

  const [dashSelectTimestamp, setDashSelectTimestamp] = useState(0);

  const [propsFS, setPropsFS] = useState(props);

  const onClick = () => {
    if ((!isDragging.current && isMobile) || (!isMobile && type === GraphType.dashboard))
      openFullscreen();
  };

  const dropdownItems = useMemo<MenuProps>(() => {
    const items: ItemType[] = [];
    // CSV export (exporter API) is item based, custom sources (netflow) are not supported yet
    if (!box.custom) {
      items.push({
        key: 'csv',
        icon: <MdOutlineFileDownload />,
        label: 'CSV',
        title: 'Download CSV',
        onClick: () => {
          if (!GLOB.isValidEnterprise()) {
            Log.warn(<div style={{ width: '38em' }}>Export to CSV is available in the <strong>Enterprise Edition</strong> of XorMon.
              <br />For more information, please visit our website <a href="https://xormon.com/support-ng.php" target="_blank" rel="noopener noreferrer">https://xormon.com/support-ng.php</a></div>);
            return;
          }
          const visibleUids = data.filter(pd => pd.visible === true).map(pd => pd.meta.uid);
          axios.post<Blob, AxiosResponse<Blob>, ExportTimeseriesRequestDTO>('/api/exporter/v1/timeseries',
            {
              start: state.startSec,
              end: state.endSec,
              uuids: graphUtil.isMetricItems() ? box.uuids : visibleUids,
              metric: graphUtil.isMetricItems() ? visibleUids : box.metrics.map(m => m.metric),
              graphProperties: box.properties
            },
            {
              responseType: 'blob', signal: aborter.current.signal,
              params: {
                format: 'csv'
              }
            }).then(response => {
              void saveBlobToFile(response.data, getFileName() + '.csv');
            }, (reason: AxiosError) => Log.error('Failed to export plot to CSV!', reason));
        }
      });
    }

    if (isPeakMetricAllowed()) {
      const style: React.CSSProperties = { backgroundColor: undefined };
      items.push({
        key: 'max',
        icon: <MdOutlineVerticalAlignTop />,
        label: 'MAX',
        title: 'Toggle peak metric',
        style,
        onClick: () => {
          state.peakMetric = !state.peakMetric;
          setToggled(style, state.peakMetric);
          void requestData(state.startSec, state.endSec);
        }
      });
    }

    if (type === GraphType.regular && props.interval) {
      items.push({
        key: 'fullscreen',
        icon: <FaExpand />,
        label: 'Fullscreen',
        title: 'Show graph in fullscreen',
        onClick: openFullscreen
      });
    }

    if (GraphPlotUtil.isOthersAllowed(box)) {
      items.push({
        key: BTN_NAME_LOAD_ALL,
        icon: <FaAngleDoubleDown />,
        label: 'Top items',
        title: 'Load top items',
        onClick: () => setModalOtherOpen(true)
      });
    }

    if (common?.availableMetrics?.length && graphUtil.isMetricItems()) {
      items.push({
        key: 'secondyaxis',
        icon: <MdLineAxis />,
        label: 'Y-axis',
        title: 'Additional Y-axis',
        onClick: () => setModalAxisOpen(true)
      });
    }

    if (box.metrics?.some(m => m.stacked)) {
      const style: React.CSSProperties = { backgroundColor: undefined };
      items.push({
        key: 'stacked',
        icon: <FaChartLine />,
        label: 'Stacked',
        title: 'Toggle stacked',
        style,
        onClick: () => {
          state.stacked = !state.stacked;
          setToggled(style, !state.stacked);
          void requestData(state.startSec, state.endSec);
        }
      });
    }

    if (!graphUtil.isMetricItems() && !box.withoutDeleted) {
      items.push({
        key: 'deleted',
        icon: <FaRegTrashAlt />,
        label: 'Deleted',
        title: 'Deleted items',
        onClick: () => setModalDeletedOpen(true)
      });
    }

    return { items };
  }, [data]);

  const [config] = useState(() => {
    const modeBarButtonsToAdd: ModeBarButton[] = [];

    if (!isRegroupActive && common?.dashUrl && type === GraphType.regular && props.interval && GLOB.selectedItem.titleText?.toLowerCase() !== 'view') {
      modeBarButtonsToAdd.push({
        name: 'favorite',
        attr: 'favorite',
        title: 'Manage in dashboards',
        icon: convertIconToPlotly(FaStar),
        click: () => setDashSelectTimestamp(Date.now())
      });
    }
    const cfg: Partial<Config> = {
      displaylogo: false, autosizable: true, responsive: true, displayModeBar: type !== GraphType.dashboard,
      modeBarButtons: [['resetScale2d'], modeBarButtonsToAdd],
      scrollZoom: true, showAxisDragHandles: true,
      toImageButtonOptions: { filename: getFileName() }
    };
    return cfg;
  });

  const [layout] = useState(() => {
    const pl = createPlotlyLayout(props);
    if (hover) state.hoverMode = pl.hovermode = 'x';
    else pl.hovermode = state.hoverMode;

    if (isMobile && type !== GraphType.modal) pl.dragmode = false;
    if (graphHeightOverride && type === GraphType.regular) pl.height = graphHeightOverride;
    return pl;
  });

  const onResize = useCallback(() => {
    if (!state.plotlyRef?._fullData?.length)
      return;
    if (type === GraphType.dashboard && props.interval === GRAPH_INTERVAL.day) {
      layout.xaxis.tickformat = state.plotlyRef._fullLayout.width < 250 ? '%H' : null;
    }
    Plotly.Plots.resize(state.plotlyRef);
  }, [state.plotlyRef]);
  const { ref } = useResizeDetector<HTMLDivElement>({ onResize, skipOnMount: true, refreshMode: 'debounce' });

  const postTimeserie = (request: TimeseriesRequestDTO) => {
    const { pathname } = GLOB.location;
    // Read at request time: a useSearchParams subscription would re-render every graph on the
    // page, hidden tabs included, on each hash change (tab switch).
    const searchParams = new URLSearchParams(GLOB.location.search);
    request.custom = box.custom;
    request.path = searchParams.get('group_id');
    if (!request.path && pathname.includes('group') && !pathname.includes('rgroup') && !pathname.includes('_group') && !GLOB.selectedGroupId) {
      request.path = searchParams.get('item_id');
    }
    return postApi<TimeseriesResponseDTO, TimeseriesRequestDTO>(API_URL.METRICS + '/data/timeseries', request,
      {
        signal: dataAborter.current.signal, params: {
          parent: box.parent?.subsystem,
          top: state.loadedItems,
          order: state.orderBy,
          fixed: state.uuids,
          path: GLOB.location.pathname,
          deleted: (graphUtil.isMetricItems() || box.withoutDeleted) ? 'hide' : state.deletedItems,
          skipNull: box.skipNull,
          interval: box.overrideInterval
        } as TimeseriesRequestParams
      });
  };

  useEffect(() => {
    aborter.current = new AbortController();
    return () => {
      aborter.current.abort();
      dataAborter.current?.abort();
    };
  }, []);

  const uuidsKey = box.uuids?.join(',');

  useEffect(() => {
    if (!zooming) {
      if (props.state?.additionalYaxis)
        void confirmAdditionalYaxisMetric(props.state.additionalYaxis, true);
      if (props.state?.startSec && props.start != props.state?.startSec || props.state?.endSec && props.end != props.state?.endSec)
        zoom(props.state.startSec * 1000, props.state.endSec * 1000);
      else if (props.data) {
        void requestData(props.state.startSec, props.state.endSec);
      } else {
        void init();
      }
    }
  }, [props.start, props.end, props.data, uuidsKey]);

  useEffect(() => {
    if (props.interval && zooming) {
      clearZoom();
    }
  }, [props.interval]);

  useEffect(() => {
    if (regroupedProps?.zoomRange) {
      if (state.startSec === regroupedProps.zoomRange.start && state.endSec === regroupedProps.zoomRange.end) return;
      const start = regroupedProps.zoomRange.start * 1000;
      const end = regroupedProps.zoomRange.end * 1000;
      layout.xaxis.range = [dayjs.tz(start).format(X_FORMAT), dayjs.tz(end).format(X_FORMAT)];
      releaseSharedYRange();
      zoom(start, end);
    } else if (zooming) {
      clearZoom();
    }
  }, [regroupedProps?.zoomRange]);

  // Not in GraphTable, which is unmounted while the legend is off.
  const applyHiddenItems = useEffectEvent((hiddenItems: readonly string[]) => {
    // not loaded yet: updateData applies the list
    if (!data.length || !graphUtil.applyHiddenItems(data, hiddenItems)) return;
    assignFixedUIDs();
    assignTickFormat(tableData);
    setData([...data]);
    setTableData([...tableData]);
    refreshPlots();
  });

  useEffect(() => {
    applyHiddenItems(regroupedProps.hiddenItems);
  }, [regroupedProps.hiddenItems]);

  useEffect(() => {
    if (!initialized.current || !hover?.sourceID || hover?.sourceID === plotID) return;
    if (hover?.xval)
      Plotly.Fx.hover(plotID, { xval: hover?.xval }, 'xy', true);
    else
      Plotly.Fx.unhover(plotID);
  }, [hover?.xval]);

  useEffect(() => {
    if (!common?.minYTop) return;
    layout.yaxis.range = [0, common.minYTop];
    layout.yaxis.autorangeoptions = { include: common.minYTop };
    setData(prev => [...prev]);
  }, [common?.minYTop]);

  useEffect(() => {
    if (!sharedActive || !data.length) return;
    const extremes = GraphPlotUtil.plotExtremes(data);
    // minimal Y range thresholds of this graph apply to the whole unit group
    const max = Math.max(extremes.max, getMinYRange(box.metrics, extremes.max)?.[1] ?? 0);
    sharedY.report(plotID, yUnitKey, { min: extremes.min, max });
  }, [data]);

  useEffect(() => {
    if (!sharesYRange) return;
    return () => sharedY.report(plotID, yUnitKey, null);
  }, []);

  useEffect(() => {
    if (!sharedActive || !data.length) return;
    assignTickFormat(tableData, data);
    setData(prev => [...prev]);
  }, [sharedExtremes?.max]);

  function isPeakMetricAllowed() {
    return box.peakMetric && props.interval !== GRAPH_INTERVAL.day;
  }

  function openFullscreen() {
    const plainTitle = type === GraphType.dashboard || isMobile;
    setPropsFS({
      ...props,
      box: { ...box, graphLabel: GraphPlotUtil.fullScreenTitle(common?.tabName, box.graphLabel || props.title, plainTitle) },
      type: GraphType.modal,
      title: props.title,
      data: {
        data: structuredClone(data), tableData: tableData, max: graphUtil.maxValue,
        zoomTitle: GraphPlotUtil.fullScreenTitle(common?.tabName, (layout.title as { text: string; })?.text, plainTitle)
      },
      state: structuredClone({ ...state, plotlyRef: null })
    });
    setModalFullscreenVisible(true);
  }

  function getFileName() {
    return props.filename ? props.filename
      : `${GLOB.selectedType || GLOB.selectedClass}_${GLOB.selectedItem.titleText}_${common?.tabName}_${props.title}`.replaceAll(' ', '-').replaceAll('/', '');
  }

  function assignTickFormat(tblData: KTM[], plotData?: Partial<ScatterData>[]) {
    return GraphPlotUtil.assignTickFormat(tblData, plotData || data, props, layout, graphUtil.maxValue, sharedMaxRef.current);
  }

  function isOthersActive() {
    return GraphPlotUtil.isOthersAllowed(box) && state.loadedItems && state.loadedItems < box.uuids.length;
  }

  function assignFixedUIDs(pds: Partial<ScatterData>[] = data) {
    if (isOthersActive() && pds.some(pd => pd.visible !== true)) {
      state.uuids = pds.filter(pd => pd.visible === true).map(pd => pd.meta.uid);
    } else {
      state.uuids = null;
    }
  }

  function isYaxisAutorange(metrics: Metric[]) {
    return layout.yaxis.autorange || metrics.some(m => m.unit === 'percent' && m.maxY);
  }

  function updateData(resps: AxiosResponse<TimeseriesResponseDTO>[]) {
    // read by index, the same array is reused by refreshPlots
    const [primary, additional] = resps;

    let y2: ReturnType<typeof graphUtil.updatePlotData> = { unitSize: '', plotData: [], tableData: [] };
    if (state.additionalYaxis && additional) {
      const metrics = [state.additionalYaxis as Metric];
      y2 = graphUtil.updatePlotData(additional.data.data, data, metrics, hiddenItemsRef.current);
      // may be empty when the metric has no data for the item
      y2.plotData.forEach(pd => pd.yaxis = 'y2');
      if (isYaxisAutorange(metrics)) {
        layout.yaxis2.range = getMinYRange(metrics, GraphPlotUtil.maxY(y2.plotData));
        layout.yaxis2.autorangeoptions = { include: layout.yaxis2.range?.[1] };
      } else {
        layout.yaxis2.autorangeoptions = undefined;
      }
    }

    const { series: seriesWithThresholds, metrics: metricsWithThresholds } = GraphPlotUtil.withThresholds(primary.data.data, box);
    const updated = graphUtil.updatePlotData(seriesWithThresholds, data, metricsWithThresholds, hiddenItemsRef.current);

    if (isYaxisAutorange(box.metrics)) {
      const dataMax = GraphPlotUtil.maxY(updated.plotData);
      if (common?.setMaxValue) {
        common.setMaxValue(dataMax, props.interval);
      } else {
        layout.yaxis.range = getMinYRange(box.metrics, dataMax);
        layout.yaxis.autorangeoptions = { include: layout.yaxis.range?.[1] };
      }
    } else
      layout.yaxis.autorangeoptions = undefined;

    assignTickFormat(updated.tableData, updated.plotData);
    state.unitSize = updated.unitSize;
    const triggerPeriodTraces = GraphPlotUtil.buildTriggerPeriodTraces(box, graphUtil.maxValue * 1.05);
    const plotData = [...triggerPeriodTraces, ...updated.plotData, ...y2.plotData];
    // as a square click does, so the next request keeps the shown items fixed
    if (hiddenItemsRef.current.length) assignFixedUIDs(plotData);
    setData(plotData);
    setTableData([...updated.tableData, ...y2.tableData]);
  }

  function refreshPlots() {
    GLOB.multiTimeout(() => updateData(responsesRef.current));
  }

  async function init() {
    if (props.start && props.end)
      return requestData(props.start, props.end);
  }

  function requestData(startSec: number, endSec: number) {
    dataAborter.current?.abort();
    const ctrl = dataAborter.current = new AbortController();
    state.startSec = startSec;
    state.endSec = endSec;
    setLoading(true);
    const createRequest = (metrics: string[]) => {
      const request = new TimeseriesRequestDTO();
      request.start = startSec;
      request.end = endSec;
      request.uuid = box.uuids;
      request.metrics = metrics;
      return postTimeserie(request);
    };

    const metrics = box.metrics.map(m => m.metric);
    if (state.peakMetric) metrics.push(box.peakMetric.metric);
    const requests = [createRequest(metrics)];
    if (state.additionalYaxis) {
      requests.push(createRequest([state.additionalYaxis.metric]));
    }

    if (!box.uuids?.length) {
      Log.debug('No UUID to plot!', box);
    }

    return Promise.all(requests).then(
      (resps) => {
        responsesRef.current = resps;
        return updateData(resps);
      },
      (reason: AxiosError) => {
        if (axios.isCancel(reason)) return;
        Log.error('Failed to get graph data!', reason);
      }
    ).finally(() => {
      if (dataAborter.current !== ctrl) return;
      setLoading(false);
      props.onLoadFinished?.();
    });
  }

  function onRelayout(event: Readonly<PlotRelayoutEvent>) {
    if (event.autosize) return;

    if (event.hovermode) {
      layout.hovermode = event.hovermode;
      refreshHoverMode();
    } else if (event['xaxis.range[0]'] && event['xaxis.range[1]']) {
      const start = Date.parse(event['xaxis.range[0]'] + '') - DateUtil.OFFSET;
      const end = Date.parse(event['xaxis.range[1]'] + '') - DateUtil.OFFSET;
      releaseSharedYRange();
      const y0 = event['yaxis.range[0]'];
      const y1 = event['yaxis.range[1]'];
      if (y0 && y1) {
        if (y0 > 0 && y1 > 0)
          layout.yaxis.range = [0, y1];
        else if (y0 < 0 && y1 < 0)
          layout.yaxis.range = [y0, 0];
        layout.yaxis.autorange = false;
      }
      zoom(start, end);
    } else if (!Object.keys(event).length || event['xaxis.autorange']) {
      clearZoom();
    } else if (Object.keys(event).some(k => Y_RANGE_KEY.test(k))) {
      // the user range replaces the shared one, no autorange handoff here
      sharedApplied.current = false;
      setYZoomed(true);
    } else if (event['yaxis.autorange']) {
      setYZoomed(false);
    }
  }

  function releaseSharedYRange() {
    if (!sharedApplied.current) return;
    sharedApplied.current = false;
    layout.yaxis.autorange = true;
  }

  function zoom(startMs: number, endMs: number) {
    setZooming(true);
    const minZoomIntervalMs = box.minInterval ? box.minInterval * 60 * 1000 : props.interval === GRAPH_INTERVAL.year ? MIN_ZOOM_INTERVAL_YEAR : MIN_ZOOM_INTERVAL;
    if (endMs - startMs <= minZoomIntervalMs) {
      startMs = endMs - minZoomIntervalMs;
      layout.xaxis.range = [dayjs.tz(startMs).format(X_FORMAT), dayjs.tz(endMs).format(X_FORMAT)];
      layout.dragmode = 'pan';
      if (!minZoomNotified.has(minZoomIntervalMs)) {
        minZoomNotified.add(minZoomIntervalMs);
        Log.info(`Minimum zoom range for this graph is ${minZoomIntervalMs > MIN_ZOOM_INTERVAL ? dayjs.duration(minZoomIntervalMs).asHours() + ' hours'
          : dayjs.duration(minZoomIntervalMs).asMinutes() + ' minutes'}.`);
      }
    } else {
      layout.dragmode = 'zoom';
    }
    setTitle(`${box.graphLabel || props.title || ''}: ${DateUtil.getShortDateTime(startMs)} - ${DateUtil.getShortDateTime(endMs)}`);
    const startSec = startMs / 1000;
    const endSec = endMs / 1000;
    syncZoomRange({ end: endSec, start: startSec });
    void requestData(startSec, endSec);
  }

  function clearZoom() {
    if (!GraphProps.isZoomAllowed(box) || !layout.title) return;
    layout.xaxis.fixedrange = false;
    layout.xaxis.autorange = true;
    layout.yaxis.autorange = true;
    layout.dragmode = isMobile && type !== GraphType.modal ? false : 'zoom';
    setTitle(box.graphLabel || props.title);
    setZooming(false);
    setYZoomed(false);
    syncZoomRange(null);
    void init();
  }

  function setTitle(text: string) {
    (layout.title as { text: string; }).text = text;
  }

  /** the fullscreen modal is a detached copy, it must not drive the sibling graphs */
  function syncZoomRange(range: StartEnd | null) {
    if (type !== GraphType.modal) regroupedProps.setZoomRange?.(range);
  }

  async function loadOtherItems(itemCount: number, order: TimeseriesTopOrder) {
    data.filter(pd => pd.visible).forEach(pd => pd.visible = true);
    state.loadedItems = getMaxTop() === itemCount && itemCount < getTopItems() ? getTopItems() : itemCount;
    state.orderBy = order;
    return requestData(state.startSec, state.endSec).then(() => setModalOtherOpen(false));
  }

  async function changeDeleted(deleted: DeletedItems) {
    state.deletedItems = deleted;
    return requestData(state.startSec, state.endSec).then(() => setModalDeletedOpen(false));
  }

  function confirmAdditionalYaxisMetric(metric: IMetric, layoutOnly = false) {
    state.additionalYaxis = metric;
    if (metric) {
      const ya = createYaxis([metric as Metric]);
      ya.overlaying = 'y';
      ya.side = 'right';
      ya.griddash = 'dot';
      // an overlaying axis defaults to 'sync', which reshapes its range to align ticks with the primary axis
      ya.tickmode = 'auto';
      ya.automargin = true;
      layout.yaxis2 = ya;
    } else {
      delete layout.yaxis2;
    }
    if (layoutOnly) return;
    return requestData(state.startSec, state.endSec).then(() => setModalAxisOpen(false));
  }

  const onPlotHover = (e: PlotHoverEvent) => {
    if (layout.hovermode !== 'x') return;
    // Plotly reports date x values as strings, Fx.hover on the synced graphs needs the numeric calc value;
    // the plotted times already carry the display offset, so the string is parsed as UTC without adjustment
    const xval = e.xvals[0];
    hover?.setVals?.(typeof xval === 'string' ? dayjs.utc(xval).valueOf() : xval, plotID);
  };

  const onPlotUnhover = () => {
    hover?.setVals?.(null, plotID);
  };

  function refreshHoverMode() {
    state.hoverMode = layout.hovermode;
    data.forEach(pd => graphUtil.assignHovertemplate(pd));
    refreshPlots();
  }

  function getMaxTop() {
    return graphUtil.itemsCount.shown + graphUtil.itemsCount.others;
  }

  // Stable: an inline ref callback would re-run on every render, for every graph on the page.
  const setPlotlyRef = useCallback((el: HTMLDivElement) => {
    const ref = el as unknown as PlotEl;
    state.plotlyRef = ref;
    if (!ref || wheelGuarded.has(ref)) return;
    wheelGuarded.add(ref);

    // stop scroll zoom over the plot area and the X axis drag handles (named by compass direction);
    // caught on the way down, so it also covers drag rects Plotly re-creates on a later redraw
    const plotlyResizeAreas = ['nsew', 'ew', 'e', 'w'];
    const selector = plotlyResizeAreas.map(direction => `rect.${direction}drag.drag`).join(', ');
    ref.addEventListener('wheel', e => {
      if ((e.target as Element)?.matches?.(selector)) e.stopPropagation();
    }, true);
  }, [state]);

  const svgContainerEl = ref.current?.querySelector('.svg-container');

  if (sharedActive && sharedExtremes && (sharedExtremes.max || sharedExtremes.min)) {
    const pad = (sharedExtremes.max - sharedExtremes.min) * SHARED_Y_PAD;
    layout.yaxis.range = [sharedExtremes.min < 0 ? sharedExtremes.min - pad : 0, sharedExtremes.max + pad];
    layout.yaxis.autorange = false;
    sharedApplied.current = true;
  }

  const className = [
    'xm-graph',
    isMobile && type !== GraphType.modal && 'xm-graph-mobile',
    type === GraphType.dashboard && props.showLegend && 'xm-graph-with-legend',
    zoomed && 'xm-graph-zoomed',
    collapsedMenu && 'xm-graph-menu-collapsed',
  ].filter(Boolean).join(' ');

  return (
    <>
      {props.interval && type === GraphType.regular && common?.dashboardItems &&
        <GraphDashboardSelect timestamp={dashSelectTimestamp} common={common} interval={props.interval} />}

      <div ref={ref}
        className={className}
        onTouchStart={() => (isDragging.current = false)}
        onTouchMove={() => (isDragging.current = true)}
        onClick={onClick}
      >
        {loading && <div className="loading"><Spin /></div>}

        <TopItemsModal open={modalOtherOpen} order={state.orderBy} count={graphUtil.itemsCount.shown}
          maxCount={getMaxTop()} onConfirm={loadOtherItems} onCancel={() => setModalOtherOpen(false)} />

        <YaxisModal open={modalAxisOpen} availableMetrics={common?.availableMetrics} metric={state.additionalYaxis}
          onCancel={() => setModalAxisOpen(false)} onConfirm={confirmAdditionalYaxisMetric} />

        <DeletedItemsModal open={modalDeletedOpen} deleted={state.deletedItems} onCancel={() => setModalDeletedOpen(false)} onConfirm={changeDeleted} />

        {plotVisible ? <>
          {
            type !== GraphType.dashboard && svgContainerEl &&
            // for tooltip z-index
            createPortal(<GraphBtns dropdownItems={dropdownItems} layout={layout} refresh={refreshHoverMode}
              modebarBtns={config.modeBarButtons as ModeBarButtonAny[][]} collapsed={collapsedMenu} zoomed={zoomed} />, svgContainerEl)
          }
          <Plot ref={setPlotlyRef}
            divId={plotID} onHover={onPlotHover} onUnhover={onPlotUnhover} onInitialized={() => initialized.current = true}
            className={`xm-plotly js-plotly-plot ${common?.dashboardItems?.some(fg => fg.interval === props.interval) ? 'xm-graph-in-dash' : ''}`}
            data={data} layout={{ ...layout }} onRelayout={onRelayout}
            useResizeHandler config={config} />
        </>
          : <div style={{ height: layout.height }}><XmSpinner /></div>}

        {type === GraphType.dashboard ? (props.showLegend ? <GraphLegend data={data} plotlyRef={state.plotlyRef} /> : null)
          : props.showLegend === false ? null
            : <GraphTable graphPlotData={graphUtil} state={state} graphProps={props} data={data} setData={setData} refreshPlots={refreshPlots}
              tableData={tableData} setTableData={setTableData} assignTickFormat={assignTickFormat} setPlotVisible={setPlotVisible}
              assignFixedUIDs={assignFixedUIDs} />}
      </div>

      {type !== GraphType.modal &&
        <Modal centered open={modalFullscreenVisible} onCancel={() => setModalFullscreenVisible(false)} destroyOnHidden closable={false} footer={null} width="90%">
          <XmGraph {...propsFS} />
        </Modal>}
    </>
  );
};
